// Cliente HTTP de la terminal virtual.
//
// ES OTRO ARCHIVO Y NO EL DE SIEMPRE, por la misma razón que la tienda
// tiene el suyo, y aquí importa todavía más.
//
// `src/api.js` adjunta el token de la PERSONA a cada petición: es su
// trabajo. Esta pantalla es un APARATO, y un aparato tiene su propia
// credencial —audiencia distinta, vida larga, sin correo ni contraseña—.
// Si usara aquel, una terminal virtual abierta en la misma computadora
// donde el encargado dejó su sesión mandaría SU token a las rutas de
// aparato, y el kárdex diría que la Repisa 3 hizo lo que hizo el
// encargado. Eso es exactamente lo que T-1 vino a arreglar.
//
// Las dos credenciales NO SE MEZCLAN NUNCA, y hay pruebas que lo
// vigilan:
//
//   · Este archivo no lee la clave donde vive el token de la persona.
//   · La credencial del aparato vive bajo su propia clave.
//   · Nada de cookies: `credentials: 'omit'` va explícito para que un
//     cambio futuro del navegador no las cuele por omisión.
//
// DÓNDE SE GUARDA LA CREDENCIAL. En `localStorage`, igual que un aparato
// de verdad la guarda en su disco: si se recarga la página o se va la
// luz, la terminal vuelve a ser la misma terminal sin que nadie teclee
// los seis dígitos otra vez. Quien no quiera eso, «Olvidar esta
// terminal» la borra.

export const CLAVE_CREDENCIAL = 'tego.terminal.credencial';
export const CLAVE_APARATO = 'tego.terminal.aparato';

export class ErrorTerminal extends Error {}

export function credencialGuardada() {
  try {
    return localStorage.getItem(CLAVE_CREDENCIAL);
  } catch {
    // Un navegador con el almacenamiento bloqueado no revienta la
    // pantalla: se comporta como una terminal recién sacada de la caja.
    return null;
  }
}

export function guardarCredencial(credencial, aparato) {
  try {
    localStorage.setItem(CLAVE_CREDENCIAL, credencial);
    localStorage.setItem(CLAVE_APARATO, JSON.stringify(aparato));
  } catch { /* sin almacenamiento, la sesión dura lo que la pestaña */ }
}

export function aparatoGuardado() {
  try {
    return JSON.parse(localStorage.getItem(CLAVE_APARATO) || 'null');
  } catch {
    return null;
  }
}

export function olvidar() {
  try {
    localStorage.removeItem(CLAVE_CREDENCIAL);
    localStorage.removeItem(CLAVE_APARATO);
  } catch { /* nada que olvidar */ }
}

async function procesar(respuesta) {
  let cuerpo = null;
  try {
    cuerpo = await respuesta.json();
  } catch {
    cuerpo = null;
  }
  if (!respuesta.ok) {
    // EL MENSAJE DEL SERVIDOR SE RESPETA. El 409 de modo vitrina dice
    // «escanea el QR de la pantalla con la app de TEGO en tu teléfono»:
    // sustituirlo por un «error» genérico tira justo el trabajo que hace
    // que esta pantalla se pueda usar sin manual.
    const e = new ErrorTerminal(
      cuerpo?.detalle || cuerpo?.detail
      || 'No se pudo hablar con TEGO. Revisa la conexión de la terminal.',
    );
    e.codigo = respuesta.status;
    throw e;
  }
  return cuerpo;
}

function pedir(ruta, opciones = {}, { conCredencial = true } = {}) {
  const cabeceras = { 'Content-Type': 'application/json', ...(opciones.headers || {}) };
  if (conCredencial) {
    const c = credencialGuardada();
    if (c) cabeceras.Authorization = `Bearer ${c}`;
  }
  return fetch(`/api/dispositivos${ruta}`, {
    ...opciones,
    credentials: 'omit',
    headers: cabeceras,
  }).then(procesar);
}

// II-4 · LA RUTA DE APARATO DE INTELIGENCIA, con la MISMA credencial y
// las mismas reglas que `pedir` (sin cookies, error del servidor
// respetado). No es una ruta propia de la terminal virtual: es la hermana
// de aparato que R-II5 hizo para el aparato físico, y vive bajo otro
// prefijo porque es de otro módulo.
const PREFIJO_INTELIGENCIA = '/api/inteligencia/aparato';

function pedirInteligencia(ruta) {
  const cabeceras = { 'Content-Type': 'application/json' };
  const c = credencialGuardada();
  if (c) cabeceras.Authorization = `Bearer ${c}`;
  return fetch(`${PREFIJO_INTELIGENCIA}${ruta}`, {
    credentials: 'omit',
    headers: cabeceras,
  }).then(procesar);
}

// LAS MISMAS RUTAS QUE EL APARATO FÍSICO, sin ninguna propia. Es la
// condición del Anexo B §B8: si la terminal virtual tuviera atajos, dejar
// de servir para probar T-2 a T-4, que es para lo que existe.
export const terminal = {
  enrolar: (codigo) => pedir('/enrolar', {
    method: 'POST', body: JSON.stringify({ codigo }),
  }, { conCredencial: false }),

  miLista: () => pedir('/mi-lista'),

  pedirQR: () => pedir('/vinculacion', { method: 'POST' }),

  mirarQR: (nonce) => pedir(`/vinculacion/${encodeURIComponent(nonce)}`),

  mover: (movimientos) => pedir('/movimientos', {
    method: 'POST', body: JSON.stringify({ movimientos }),
  }),

  contar: (conteos) => pedir('/conteo', {
    method: 'POST', body: JSON.stringify({ conteos }),
  }),

  // TC-30. Los lotes de ESE producto que están EN ESTA repisa, con el
  // FIFO ya resuelto por el servidor. No se ordena aquí a propósito: si
  // lo hiciera cada cliente, la perilla física y esta pantalla
  // sugerirían lotes distintos para la misma repisa.
  lotes: (sku) => pedir(`/lotes/${encodeURIComponent(sku)}`),

  // TC-31 · EL MENÚ DEL SUPERVISOR. El aparato NO decide si quien está
  // delante es supervisor: manda su credencial y el servidor mira quién
  // hay vinculado. Si lo decidiera el cliente, bastaría un navegador con
  // la consola abierta para ver las sesiones de la sucursal.
  supervisor: () => pedir('/supervisor'),

  cerrarSesionAjena: (idSesion) => pedir('/supervisor/cerrar-sesion', {
    method: 'POST', body: JSON.stringify({ id_sesion: idSesion }),
  }),

  motivos: () => pedir('/motivos-rechazo'),

  rechazar: (rechazos) => pedir('/rechazo', {
    method: 'POST', body: JSON.stringify({ rechazos }),
  }),

  latido: (datos) => pedir('/latido', {
    method: 'POST', body: JSON.stringify(datos),
  }),

  // II-4 · «DÓNDE ACOMODARLO» al recibir. La sugerencia queda PENDIENTE
  // en TEGO; decidirla es de quien tenga la sesión (R-II5).
  dondeAcomodar: (idProducto, cantidad) => pedirInteligencia(
    `/ubicacion-sugerida?id_producto=${encodeURIComponent(idProducto)}`
    + `&cantidad=${encodeURIComponent(cantidad)}`),
};

// EL CARTEL QUE SALE DE LA RESPUESTA. Una línea para una pantalla de dos:
// dónde, y por qué. Sin respuesta útil, `null` y no se pinta nada — la
// terminal sigue igual que ayer.
export function cartelDeUbicacion(respuesta) {
  if (!respuesta) return null;
  const mejor = respuesta.sugerencias?.[0];
  if (respuesta.estado === 'OK' && mejor) {
    return {
      donde: mejor.codigo || mejor.nombre,
      nombre: mejor.nombre && mejor.nombre !== mejor.codigo ? mejor.nombre : null,
      porque: mejor.razon || null,
    };
  }
  if (respuesta.estado === 'SIN_UBICACION' && respuesta.mensaje) {
    return { donde: null, nombre: null, porque: respuesta.mensaje };
  }
  return null;
}

// CUÁNDO SE PREGUNTA: solo cuando ENTRA mercancía (el giro sube la
// existencia) y la lista trae el número del producto. Una salida o un
// conteo que baja no tienen nada que acomodar.
export function tocaPreguntarDonde(giro, producto) {
  return Boolean(giro && giro.delta > 0 && producto && producto.id_producto);
}
